import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowRight, ScrollText, GraduationCap, FileSearch, FlaskConical, Building2, Users, Database, ShieldCheck } from "lucide-react";

const groups = [
  {
    name: "Publication & Writing",
    items: [
      { icon: ScrollText, title: "Research Publication Consultation", desc: "Idea framing, journal targeting, manuscript shaping, response-to-reviewer support." },
      { icon: FileSearch, title: "Academic Writing Support", desc: "Drafting, structural editing, language polishing, submission packs." },
      { icon: ShieldCheck, title: "Ethics & Plagiarism Review", desc: "Pre-submission similarity check + authorship and COI guidance." },
    ],
  },
  {
    name: "Mentorship & Training",
    items: [
      { icon: GraduationCap, title: "Thesis Mentorship", desc: "Cohort + 1:1 mentoring for Bachelors, Masters, MPhil, and PhD candidates." },
      { icon: FlaskConical, title: "AI & Digital Research Training", desc: "Hands-on programs: literature workflows, AI tools, data methods, reproducible research." },
      { icon: Users, title: "Workshops & Bootcamps", desc: "Departmental and faculty-wide intensives across writing, ethics, and methods." },
    ],
  },
  {
    name: "Institutional & Ecosystem",
    items: [
      { icon: Building2, title: "Institutional Collaboration", desc: "Research-capacity programs co-designed with universities and colleges." },
      { icon: Database, title: "Research Ecosystem Development", desc: "Connect researchers, mentors, journals, datasets and grants in one workspace." },
    ],
  },
];

export default function Services() {
  return (
    <div className="min-h-screen bg-kb-bg" data-testid="services-page">
      <Navbar />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12 lg:pt-28">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-4">
          OUR SERVICES
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-kb-ink leading-[1.05] max-w-4xl">
          Services that <em className="italic text-kb-primary">scaffold</em><br />
          real scholarship.
        </h1>
        <p className="mt-6 text-lg text-kb-ink-muted max-w-2xl">
          Eight tightly-integrated service tracks — each backed by tools, mentors and
          accountable workflows inside our platform.
        </p>
      </section>

      {groups.map((g, gi) => (
        <section key={g.name} className="border-t border-slate-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">
                {`0${gi+1}`} / TRACK
              </div>
              <h2 className="font-serif text-2xl text-kb-ink mt-3 leading-tight">{g.name}</h2>
            </div>
            <div className="lg:col-span-9 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
              {g.items.map((it) => (
                <div key={it.title} className="bg-white p-6" data-testid={`service-item-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <it.icon className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                  <div className="font-serif text-lg text-kb-ink mt-5">{it.title}</div>
                  <div className="text-sm text-kb-ink-muted mt-2 leading-relaxed">{it.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="bg-kb-primary text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
          <h2 className="font-serif text-3xl lg:text-4xl max-w-2xl leading-tight">
            Not sure where to start?<br />
            <span className="italic text-white/70">Submit an idea, we'll guide you.</span>
          </h2>
          <Link to="/register" className="bg-white text-kb-primary px-6 py-3.5 rounded-sm font-medium inline-flex items-center gap-2" data-testid="services-cta">
            Submit your idea <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
